import React, { useState, useEffect } from 'react'
import { apiClient } from '../common/apiClient'
import { getApiUrl } from '../config/constants'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from './ui/dialog'
import { Button } from './ui/button'
import Switch from 'react-switch'
import {
  HiCog6Tooth,
  HiPlus,
  HiXMark,
  HiUserGroup,
  HiChatBubbleLeftRight,
  HiComputerDesktop,
  HiChatBubbleLeftEllipsis,
  HiSparkles,
  HiWrenchScrewdriver,
} from 'react-icons/hi2'
import { FaSnowflake } from 'react-icons/fa'

interface UserPreferencesModalProps {
  isOpen: boolean
  onClose: () => void
  userEmail: string | undefined
  userTeam?: string
  highlightSetting?: 'team_bar'
  getGraphAccessToken?: () => Promise<string | null>
}

interface SantaHatSettings {
  enabled: boolean
  x: number
  y: number
  rotation: number
  scale: number
  snowfall_enabled: boolean
  team_bar_enabled: boolean
}

interface UserPreferences {
  user_email: string
  show_coffee_badge: boolean
  team_bar_layout: 'horizontal' | 'vertical'
  show_team_bar: boolean
  show_christmas_snow: boolean
  tutorial_mode: boolean
}

interface PinnedChat {
  id: number
  chat_id: string
  chat_topic: string | null
  chat_type: string
  web_url: string | null
  sort_order: number
}

interface TeamsChat {
  id: string
  topic: string | null
  chatType: string
  webUrl: string | null
  lastUpdatedDateTime: string
  memberCount: number
  memberEmail: string | null
}

type Category = 'appearance' | 'teams' | 'system-dev'

const UserPreferencesModal: React.FC<UserPreferencesModalProps> = ({
  isOpen,
  onClose,
  userEmail,
  userTeam,
  highlightSetting,
  getGraphAccessToken,
}) => {
  const isSystemDev = userTeam === 'System Development'

  const [activeCategory, setActiveCategory] = useState<Category | null>(null)
  const [showCoffeeBadge, setShowCoffeeBadge] = useState(true)
  const [showTeamBar, setShowTeamBar] = useState(true)
  const [originalShowTeamBar, setOriginalShowTeamBar] = useState(true)
  const [showChristmasSnow, setShowChristmasSnow] = useState(false)
  const [originalShowChristmasSnow, setOriginalShowChristmasSnow] = useState(false)
  const appearanceRef = React.useRef<HTMLDivElement>(null)
  const teamsRef = React.useRef<HTMLDivElement>(null)
  const [teamBarLayout, setTeamBarLayout] = useState<'horizontal' | 'vertical'>('horizontal')
  const [originalTeamBarLayout, setOriginalTeamBarLayout] = useState<'horizontal' | 'vertical'>('horizontal')
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Pinned chats state
  const [pinnedChats, setPinnedChats] = useState<PinnedChat[]>([])
  const [showChatPicker, setShowChatPicker] = useState(false)
  const [availableChats, setAvailableChats] = useState<TeamsChat[]>([])
  const [isLoadingChats, setIsLoadingChats] = useState(false)
  const [chatError, setChatError] = useState<string | null>(null)

  // Tutorial mode (System Dev only, per-user)
  const [tutorialMode, setTutorialMode] = useState(false)

  // Santa hat settings (System Dev only)
  const [santaHatSettings, setSantaHatSettings] = useState<SantaHatSettings>({
    enabled: true,
    x: 64,
    y: 11,
    rotation: 10,
    scale: 0.32,
    snowfall_enabled: true,
    team_bar_enabled: false,
  })

  const categories = [
    { id: 'appearance' as Category, label: 'Appearance', icon: HiSparkles, isImage: false },
    ...(santaHatSettings.team_bar_enabled ? [{ id: 'teams' as Category, label: 'Teams Message Shortcut Bar', icon: '/teams_logo.png', isImage: true }] : []),
    ...(isSystemDev ? [{ id: 'system-dev' as Category, label: 'System Dev', icon: HiWrenchScrewdriver, isImage: false }] : []),
  ]
  const [showSantaControls, setShowSantaControls] = useState(false)
  const [isSavingSanta, setIsSavingSanta] = useState(false)
  const [santaSaved, setSantaSaved] = useState(false)

  // Fetch user preferences and pinned chats when modal opens
  useEffect(() => {
    const fetchPreferences = async () => {
      if (!isOpen || !userEmail) return

      setIsLoading(true)
      try {
        const [prefsData, pinnedData, santaData] = await Promise.all([
          apiClient.get<UserPreferences>(
            `/api/user-preferences?email=${encodeURIComponent(userEmail)}`
          ),
          apiClient.get<PinnedChat[]>(
            `/api/user-preferences/pinned-chats?email=${encodeURIComponent(userEmail)}`
          ),
          apiClient.get<SantaHatSettings>('/api/santa-hat/settings'),
        ])
        setShowCoffeeBadge(prefsData.show_coffee_badge)
        const showTeamBarValue = prefsData.show_team_bar ?? true
        setShowTeamBar(showTeamBarValue)
        setOriginalShowTeamBar(showTeamBarValue)
        const layoutValue = prefsData.team_bar_layout ?? 'horizontal'
        setTeamBarLayout(layoutValue)
        setOriginalTeamBarLayout(layoutValue)
        const showSnowValue = prefsData.show_christmas_snow ?? false
        setShowChristmasSnow(showSnowValue)
        setOriginalShowChristmasSnow(showSnowValue)
        setTutorialMode(prefsData.tutorial_mode ?? false)
        setPinnedChats(pinnedData || [])
        setSantaHatSettings({
          ...santaData,
          scale: parseFloat(String(santaData.scale)),
        })
      } catch (error) {
        console.error('Error fetching user preferences:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchPreferences()
  }, [isOpen, userEmail])

  const handleLayoutChange = (layout: 'horizontal' | 'vertical') => {
    setTeamBarLayout(layout)
    window.dispatchEvent(new CustomEvent('user-preferences-changed', { detail: { team_bar_layout: layout } }))
  }

  const handleShowTeamBarChange = (show: boolean) => {
    setShowTeamBar(show)
    window.dispatchEvent(new CustomEvent('user-preferences-changed', { detail: { show_team_bar: show } }))
  }

  const handleShowChristmasSnowChange = (show: boolean) => {
    setShowChristmasSnow(show)
    window.dispatchEvent(new CustomEvent('user-preferences-changed', { detail: { show_christmas_snow: show } }))
  }

  const toggleCategory = (category: Category) => {
    if (activeCategory === category) {
      setActiveCategory(null) // Toggle off - show all
    } else {
      setActiveCategory(category) // Toggle on - show only this category
    }
  }

  const handleOpenChatPicker = async () => {
    if (!getGraphAccessToken) {
      setChatError('Authentication not available')
      return
    }

    setShowChatPicker(true)
    setIsLoadingChats(true)
    setChatError(null)

    try {
      const token = await getGraphAccessToken()
      if (!token) {
        setChatError('Could not get access token')
        setIsLoadingChats(false)
        return
      }

      const response = await fetch(`${getApiUrl()}/api/auth/teams-chats`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessToken: token, top: 20 }),
      })

      if (!response.ok) {
        setChatError('Failed to fetch chats. You may need to re-login.')
        setIsLoadingChats(false)
        return
      }

      const data = await response.json()
      setAvailableChats(data.chats || [])
    } catch (error) {
      console.error('Error fetching Teams chats:', error)
      setChatError('Failed to fetch chats')
    } finally {
      setIsLoadingChats(false)
    }
  }

  const handleAddPinnedChat = async (chat: TeamsChat) => {
    if (!userEmail) return

    try {
      const newPinned = await apiClient.post<PinnedChat>('/api/user-preferences/pinned-chats', {
        email: userEmail,
        chat_id: chat.id,
        chat_topic: chat.topic,
        chat_type: chat.chatType,
        web_url: chat.webUrl,
        member_email: chat.memberEmail,
      })
      setPinnedChats([...pinnedChats, newPinned])
      window.dispatchEvent(new CustomEvent('pinned-chats-changed'))
    } catch (error) {
      console.error('Error adding pinned chat:', error)
    }
  }

  const handleRemovePinnedChat = async (chatId: string) => {
    if (!userEmail) return

    try {
      await apiClient.delete(
        `/api/user-preferences/pinned-chats/${encodeURIComponent(chatId)}?email=${encodeURIComponent(userEmail)}`
      )
      setPinnedChats(pinnedChats.filter(c => c.chat_id !== chatId))
      window.dispatchEvent(new CustomEvent('pinned-chats-changed'))
    } catch (error) {
      console.error('Error removing pinned chat:', error)
    }
  }

  const handleCancel = () => {
    if (teamBarLayout !== originalTeamBarLayout || showTeamBar !== originalShowTeamBar || showChristmasSnow !== originalShowChristmasSnow) {
      window.dispatchEvent(new CustomEvent('user-preferences-changed', { detail: { team_bar_layout: originalTeamBarLayout, show_team_bar: originalShowTeamBar, show_christmas_snow: originalShowChristmasSnow } }))
    }
    onClose()
  }

  const handleSave = async () => {
    if (!userEmail) return

    setIsSaving(true)
    try {
      await apiClient.put('/api/user-preferences', {
        email: userEmail,
        show_coffee_badge: showCoffeeBadge,
        show_team_bar: showTeamBar,
        team_bar_layout: teamBarLayout,
        show_christmas_snow: showChristmasSnow,
        tutorial_mode: tutorialMode,
      })
      setOriginalShowTeamBar(showTeamBar)
      setOriginalTeamBarLayout(teamBarLayout)
      setOriginalShowChristmasSnow(showChristmasSnow)
      // Notify other components that preferences were saved
      window.dispatchEvent(new CustomEvent('user-preferences-saved'))
      onClose()
    } catch (error) {
      console.error('Error saving user preferences:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const renderAppearanceSettings = () => (
    <div ref={appearanceRef} className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <HiSparkles className="w-4 h-4 text-gray-500" />
        <h3 className="text-sm font-semibold text-gray-700">Appearance</h3>
      </div>
      {/* Christmas Snow Toggle - only show if globally enabled */}
      {santaHatSettings.snowfall_enabled && (
        <div className="flex items-center justify-between gap-4 py-3 px-4 bg-gray-50 rounded-lg">
          <div className="flex-1 flex items-center gap-3">
            <FaSnowflake className="w-5 h-5 text-sky-400 flex-shrink-0" />
            <div>
              <label className="text-sm font-medium text-gray-900">Christmas snow effect</label>
              <p className="text-xs text-gray-500 mt-0.5">Add a festive snowfall animation to the background</p>
            </div>
          </div>
          <Switch
            checked={showChristmasSnow}
            onChange={handleShowChristmasSnowChange}
            onColor="#3b82f6"
            offColor="#d1d5db"
            checkedIcon={false}
            uncheckedIcon={false}
            height={24}
            width={48}
            handleDiameter={20}
          />
        </div>
      )}
      {/* Coffee Badge Toggle */}
      <div className="flex items-center justify-between gap-4 py-3 px-4 bg-gray-50 rounded-lg">
        <div className="flex-1">
          <label className="text-sm font-medium text-gray-900">Coffee order indicator</label>
          <p className="text-xs text-gray-500 mt-0.5">If you don't drink coffee you can disable the coffee notification status in the top bar</p>
        </div>
        <Switch
          checked={showCoffeeBadge}
          onChange={setShowCoffeeBadge}
          onColor="#3b82f6"
          offColor="#d1d5db"
          checkedIcon={false}
          uncheckedIcon={false}
          height={24}
          width={48}
          handleDiameter={20}
        />
      </div>
    </div>
  )

  const renderTeamsSettings = () => (
    <div ref={teamsRef} className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <img src="/teams_logo.png" alt="" className="w-4 h-4 object-contain" />
        <h3 className="text-sm font-semibold text-gray-700">Teams Message Shortcut Bar</h3>
      </div>
      {/* Enable/Disable Team Bar */}
      <div className="flex items-center justify-between gap-4 py-3 px-4 bg-gray-50 rounded-lg">
        <div className="flex-1">
          <label className="text-sm font-medium text-gray-900">Enable shortcut bar</label>
          <p className="text-xs text-gray-500 mt-0.5">Show the floating Teams shortcut bar</p>
        </div>
        <Switch
          checked={showTeamBar}
          onChange={handleShowTeamBarChange}
          onColor="#3b82f6"
          offColor="#d1d5db"
          checkedIcon={false}
          uncheckedIcon={false}
          height={24}
          width={48}
          handleDiameter={20}
        />
      </div>
      {/* Orientation */}
      <div className={`flex items-center justify-between gap-4 py-3 px-4 bg-gray-50 rounded-lg transition-opacity ${!showTeamBar ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="flex-1">
          <label className="text-sm font-medium text-gray-900">Orientation</label>
          <p className="text-xs text-gray-500 mt-0.5">Choose how the shortcut bar expands</p>
        </div>
        <div className="flex gap-1 bg-white rounded-lg p-1 shadow-sm">
          <button
            onClick={() => handleLayoutChange('horizontal')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              teamBarLayout === 'horizontal'
                ? 'bg-blue-500 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Horizontal
          </button>
          <button
            onClick={() => handleLayoutChange('vertical')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              teamBarLayout === 'vertical'
                ? 'bg-blue-500 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Vertical
          </button>
        </div>
      </div>
      {/* Pinned Chats */}
      <div className={`py-3 px-4 bg-gray-50 rounded-lg transition-opacity ${!showTeamBar ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <label className="text-sm font-medium text-gray-900">Pinned chats</label>
            <p className="text-xs text-gray-500 mt-0.5">Quick access to your favourite Teams chats</p>
          </div>
          <button
            onClick={handleOpenChatPicker}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-md transition-colors"
          >
            <HiPlus className="w-3.5 h-3.5" />
            Add Chat
          </button>
        </div>

        {/* Current pinned chats */}
        {pinnedChats.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {pinnedChats.map((chat) => (
              <div
                key={chat.chat_id}
                className="flex items-center gap-1.5 pl-2 pr-1 py-1 bg-white rounded-full shadow-sm border border-gray-200"
              >
                <div className="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                  {chat.chat_type === 'group' || chat.chat_type === 'meeting' ? (
                    <HiUserGroup className="w-3 h-3 text-purple-600" />
                  ) : (
                    <HiChatBubbleLeftRight className="w-3 h-3 text-purple-600" />
                  )}
                </div>
                <span className="text-xs text-gray-700">{chat.chat_topic || 'Unnamed chat'}</span>
                <button
                  onClick={() => handleRemovePinnedChat(chat.chat_id)}
                  className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                >
                  <HiXMark className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 bg-white rounded-lg">
            <HiChatBubbleLeftEllipsis className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">No pinned chats yet</p>
            <p className="text-xs text-gray-400 mt-1">Click "Add Chat" to get started</p>
          </div>
        )}

        {/* Chat picker dropdown */}
        {showChatPicker && (
          <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden bg-white">
            <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
              <span className="text-xs font-medium text-gray-600">Your recent chats</span>
              <button onClick={() => setShowChatPicker(false)} className="p-0.5 text-gray-400 hover:text-gray-600">
                <HiXMark className="w-4 h-4" />
              </button>
            </div>
            <div className="max-h-48 overflow-y-auto">
              {isLoadingChats ? (
                <div className="flex items-center justify-center py-6">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
                </div>
              ) : chatError ? (
                <div className="px-3 py-4 text-center">
                  <p className="text-sm text-red-600">{chatError}</p>
                </div>
              ) : availableChats.length === 0 ? (
                <div className="px-3 py-4 text-center">
                  <p className="text-sm text-gray-500">No chats found</p>
                </div>
              ) : (
                availableChats
                  .filter((chat) => !pinnedChats.some((p) => p.chat_id === chat.id))
                  .map((chat) => (
                    <button
                      key={chat.id}
                      onClick={() => handleAddPinnedChat(chat)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-0"
                    >
                      <div className="flex-shrink-0 w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                        {chat.chatType === 'group' || chat.chatType === 'meeting' ? (
                          <HiUserGroup className="w-3.5 h-3.5 text-gray-600" />
                        ) : (
                          <HiChatBubbleLeftRight className="w-3.5 h-3.5 text-gray-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-700 truncate">{chat.topic || 'Unnamed chat'}</p>
                        <p className="text-xs text-gray-400">
                          {chat.chatType === 'oneOnOne' ? '1:1 chat' : `${chat.memberCount} members`}
                        </p>
                      </div>
                      <HiPlus className="w-4 h-4 text-blue-500" />
                    </button>
                  ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )

  const handleSantaSettingChange = async (key: keyof SantaHatSettings, value: number | boolean) => {
    const newSettings = { ...santaHatSettings, [key]: value }
    setSantaHatSettings(newSettings)
    // Broadcast update in real-time
    window.dispatchEvent(new CustomEvent('santa-hat-settings-updated', { detail: newSettings }))

    // Auto-save boolean settings (enabled/snowfall_enabled)
    if (typeof value === 'boolean') {
      try {
        await apiClient.put('/api/santa-hat/settings', {
          ...newSettings,
          updated_by: userEmail,
        })
      } catch (error) {
        console.error('Error saving santa hat settings:', error)
      }
    }
  }

  const handleSaveSantaSettings = async () => {
    setIsSavingSanta(true)
    setSantaSaved(false)
    try {
      await apiClient.put('/api/santa-hat/settings', {
        ...santaHatSettings,
        updated_by: userEmail,
      })
      // Broadcast final saved settings
      window.dispatchEvent(new CustomEvent('santa-hat-settings-updated', { detail: santaHatSettings }))
      setSantaSaved(true)
      setTimeout(() => setSantaSaved(false), 2000)
    } catch (error) {
      console.error('Error saving santa hat settings:', error)
    } finally {
      setIsSavingSanta(false)
    }
  }

  const btnStyle: React.CSSProperties = {
    padding: '4px 8px',
    backgroundColor: '#e5e7eb',
    color: '#374151',
    border: 'none',
    borderRadius: 4,
    fontSize: 11,
    cursor: 'pointer',
  }

  const renderSystemDevSettings = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <HiWrenchScrewdriver className="w-4 h-4 text-gray-500" />
        <h3 className="text-sm font-semibold text-gray-700">System Development</h3>
      </div>

      {/* Tutorial Mode Toggle */}
      <div className="flex items-center justify-between gap-4 py-3 px-4 bg-gray-50 rounded-lg">
        <div className="flex-1 flex items-center gap-3">
          <HiComputerDesktop className="w-5 h-5 text-blue-500 flex-shrink-0" />
          <div>
            <label className="text-sm font-medium text-gray-900">Tutorial Mode</label>
            <p className="text-xs text-gray-500 mt-0.5">Enable tutorial hints and guided walkthroughs</p>
          </div>
        </div>
        <Switch
          checked={tutorialMode}
          onChange={setTutorialMode}
          onColor="#3b82f6"
          offColor="#d1d5db"
          checkedIcon={false}
          uncheckedIcon={false}
          height={24}
          width={48}
          handleDiameter={20}
        />
      </div>

      {/* Master Team Bar Toggle */}
      <div className="flex items-center justify-between gap-4 py-3 px-4 bg-gray-50 rounded-lg">
        <div className="flex-1 flex items-center gap-3">
          <img src="/teams_logo.png" alt="" className="w-5 h-5 object-contain flex-shrink-0" />
          <div>
            <label className="text-sm font-medium text-gray-900">Enable Teams Shortcut Bar for Users</label>
            <p className="text-xs text-gray-500 mt-0.5">Master toggle - if disabled, the shortcut bar and its preferences are hidden from all users</p>
          </div>
        </div>
        <Switch
          checked={santaHatSettings.team_bar_enabled}
          onChange={(checked) => handleSantaSettingChange('team_bar_enabled', checked)}
          onColor="#3b82f6"
          offColor="#d1d5db"
          checkedIcon={false}
          uncheckedIcon={false}
          height={24}
          width={48}
          handleDiameter={20}
        />
      </div>

      {/* Master Snowfall Toggle */}
      <div className="flex items-center justify-between gap-4 py-3 px-4 bg-gray-50 rounded-lg">
        <div className="flex-1 flex items-center gap-3">
          <FaSnowflake className="w-5 h-5 text-sky-400 flex-shrink-0" />
          <div>
            <label className="text-sm font-medium text-gray-900">Enable Snowfall for Users</label>
            <p className="text-xs text-gray-500 mt-0.5">Master toggle - if disabled, snowfall option is hidden from all users</p>
          </div>
        </div>
        <Switch
          checked={santaHatSettings.snowfall_enabled}
          onChange={(checked) => handleSantaSettingChange('snowfall_enabled', checked)}
          onColor="#3b82f6"
          offColor="#d1d5db"
          checkedIcon={false}
          uncheckedIcon={false}
          height={24}
          width={48}
          handleDiameter={20}
        />
      </div>

      {/* Santa Hat Settings */}
      <div className="py-3 px-4 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <img src="/santa_hat.png" alt="Santa Hat" className="w-5 h-5 flex-shrink-0" />
            <div>
              <label className="text-sm font-medium text-gray-900">Santa Hat</label>
              <p className="text-xs text-gray-500 mt-0.5">Show santa hat on the logo (global setting)</p>
            </div>
          </div>
          <Switch
            checked={santaHatSettings.enabled}
            onChange={(checked) => handleSantaSettingChange('enabled', checked)}
            onColor="#3b82f6"
            offColor="#d1d5db"
            checkedIcon={false}
            uncheckedIcon={false}
            height={24}
            width={48}
            handleDiameter={20}
          />
        </div>

        {/* Show Controls Toggle */}
        <div className="flex items-center justify-between py-2 border-t border-gray-200">
          <label className="text-xs font-medium text-gray-700">Show position controls</label>
          <Switch
            checked={showSantaControls}
            onChange={setShowSantaControls}
            onColor="#3b82f6"
            offColor="#d1d5db"
            checkedIcon={false}
            uncheckedIcon={false}
            height={20}
            width={40}
            handleDiameter={16}
          />
        </div>

        {/* Position Controls */}
        {showSantaControls && (
          <div className="mt-3 pt-3 border-t border-gray-200 space-y-3">
            {/* X Position */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-600">X Position: {santaHatSettings.x}px</span>
              </div>
              <div className="flex gap-1">
                <button onClick={() => handleSantaSettingChange('x', santaHatSettings.x - 10)} style={btnStyle}>-10</button>
                <button onClick={() => handleSantaSettingChange('x', santaHatSettings.x - 1)} style={btnStyle}>-1</button>
                <button onClick={() => handleSantaSettingChange('x', santaHatSettings.x + 1)} style={btnStyle}>+1</button>
                <button onClick={() => handleSantaSettingChange('x', santaHatSettings.x + 10)} style={btnStyle}>+10</button>
              </div>
            </div>

            {/* Y Position */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-600">Y Position: {santaHatSettings.y}px</span>
              </div>
              <div className="flex gap-1">
                <button onClick={() => handleSantaSettingChange('y', santaHatSettings.y - 10)} style={btnStyle}>-10</button>
                <button onClick={() => handleSantaSettingChange('y', santaHatSettings.y - 1)} style={btnStyle}>-1</button>
                <button onClick={() => handleSantaSettingChange('y', santaHatSettings.y + 1)} style={btnStyle}>+1</button>
                <button onClick={() => handleSantaSettingChange('y', santaHatSettings.y + 10)} style={btnStyle}>+10</button>
              </div>
            </div>

            {/* Rotation */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-600">Rotation: {santaHatSettings.rotation}°</span>
              </div>
              <div className="flex gap-1">
                <button onClick={() => handleSantaSettingChange('rotation', santaHatSettings.rotation - 15)} style={btnStyle}>-15°</button>
                <button onClick={() => handleSantaSettingChange('rotation', santaHatSettings.rotation - 5)} style={btnStyle}>-5°</button>
                <button onClick={() => handleSantaSettingChange('rotation', santaHatSettings.rotation + 5)} style={btnStyle}>+5°</button>
                <button onClick={() => handleSantaSettingChange('rotation', santaHatSettings.rotation + 15)} style={btnStyle}>+15°</button>
              </div>
            </div>

            {/* Scale */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-600">Scale: {santaHatSettings.scale.toFixed(2)}x</span>
              </div>
              <div className="flex gap-1 flex-wrap">
                <button onClick={() => handleSantaSettingChange('scale', Math.max(0.1, santaHatSettings.scale - 0.1))} style={btnStyle}>-0.1</button>
                <button onClick={() => handleSantaSettingChange('scale', Math.max(0.1, santaHatSettings.scale - 0.01))} style={btnStyle}>-0.01</button>
                <button onClick={() => handleSantaSettingChange('scale', santaHatSettings.scale + 0.01)} style={btnStyle}>+0.01</button>
                <button onClick={() => handleSantaSettingChange('scale', santaHatSettings.scale + 0.1)} style={btnStyle}>+0.1</button>
              </div>
            </div>

            {/* Save Button */}
            <button
              onClick={handleSaveSantaSettings}
              disabled={isSavingSanta}
              className={`w-full mt-2 py-2 text-xs font-medium text-white rounded-md transition-colors ${
                santaSaved ? 'bg-green-500' : 'bg-blue-500 hover:bg-blue-600'
              } ${isSavingSanta ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isSavingSanta ? 'Saving...' : santaSaved ? 'Saved!' : 'Save Santa Hat Position'}
            </button>
          </div>
        )}
      </div>
    </div>
  )

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="max-w-[calc(100%-2rem)] md:max-w-3xl p-0 gap-0 overflow-hidden flex flex-col max-h-[85vh]">
        {/* Header - Fixed */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <HiCog6Tooth className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold text-gray-900">Preferences</DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                Customize your Duo Hub experience
              </DialogDescription>
            </div>
          </div>
        </div>

        {/* Mobile category tabs - shown on small screens */}
        <div className="flex-shrink-0 md:hidden border-b border-gray-100 bg-gray-50 px-4 py-2 overflow-x-auto">
          <div className="flex gap-2">
            {categories.map((category) => {
              const isActive = activeCategory === category.id
              return (
                <button
                  key={category.id}
                  onClick={() => toggleCategory(category.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                    isActive
                      ? 'bg-blue-500 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {category.isImage ? (
                    <img src={category.icon as string} alt="" className="w-3.5 h-3.5 flex-shrink-0 object-contain" />
                  ) : (
                    <category.icon className="w-3.5 h-3.5 flex-shrink-0" />
                  )}
                  <span>{category.label}</span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Content with sidebar - Scrollable */}
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Sidebar - hidden on mobile */}
          <div className="hidden md:block flex-shrink-0 bg-gray-50 border-r border-gray-100 p-3 overflow-y-auto">
            <nav className="space-y-1">
              {categories.map((category) => {
                const isActive = activeCategory === category.id
                return (
                  <button
                    key={category.id}
                    onClick={() => toggleCategory(category.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors text-left ${
                      isActive
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                    }`}
                  >
                    {category.isImage ? (
                      <img src={category.icon as string} alt="" className="w-4 h-4 flex-shrink-0 object-contain" />
                    ) : (
                      <category.icon className="w-4 h-4 flex-shrink-0" />
                    )}
                    <span className="text-left whitespace-nowrap">{category.label}</span>
                  </button>
                )
              })}
            </nav>
          </div>

          {/* Main content - Scrollable */}
          <div className="flex-1 p-4 md:p-5 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
              </div>
            ) : (
              <div className="space-y-8">
                {(activeCategory === null || activeCategory === 'appearance') && renderAppearanceSettings()}
                {santaHatSettings.team_bar_enabled && (activeCategory === null || activeCategory === 'teams') && renderTeamsSettings()}
                {isSystemDev && (activeCategory === null || activeCategory === 'system-dev') && renderSystemDevSettings()}
              </div>
            )}
          </div>
        </div>

        {/* Footer - Fixed */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex items-center justify-end gap-3">
          <Button variant="secondary" size="sm" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} disabled={isSaving || isLoading}>
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default UserPreferencesModal
